import * as _ from "lodash";
import { DimensionType, IHierarchy, IHierarchyLevel, IHierarchyNode } from "../PA.Core/PA.Core.IF.Hierarchy";
import { WB_PlanProductGroupRow, WB_PlanRow, WB_ProductGroupRow } from '../PA.Core/PAdbSchemaRows'
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { IMessageProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { esWB_Plan, esWB_Plan_Product_Group, esWB_Product_Group } from '../PA.Core/PAdbSchema'

export interface CreateOptions {
    bSortById?: boolean,
    dbContext?: PAdbContext
}

export class PEAPlanProductHierarchy implements IHierarchy {
    private wbPlanId: number;
    private planRow: WB_PlanRow
    private dbContext: PAdbContext;
    private bSortById: boolean

    private dctHierarchyLevels: { [LevelId: string]: IHierarchyLevel };
    private dctNodes: { [NodeId: string]: IHierarchyNode };
    private dctNodesByLevel: { [LevelId: string]: IHierarchyNode[] };

    private dctNodeParentByChildId: { [NodeId: string]: IHierarchyNode };
    private dctNodeChildrenByParentId: { [NodeId: string]: IHierarchyNode[] };
    private arrTopLevelNodes: IHierarchyNode[];

    private _loadMessageProvider: MessageProvider;
    private _OnLoadComplete: Promise<void>;
    private _bLoaded: boolean;

    constructor(wbPlanId: number, options?: CreateOptions) {
        this.wbPlanId = wbPlanId;
        this._bLoaded = false;
        this._loadMessageProvider = new MessageProvider();
        this.dctHierarchyLevels = {};
        this.dctNodes = {};
        this.dctNodesByLevel = {};
        this.dctNodeParentByChildId = {};
        this.dctNodeChildrenByParentId = {};
        this.arrTopLevelNodes = [];

        const opts = options || {}
        this.bSortById = !!opts.bSortById
        this.dbContext = opts?.dbContext

        if (_.isEmpty(this.dbContext))
            this.dbContext = new PAdbContext()
    }

    get DimensionType(): DimensionType {
        return DimensionType.Product;
    }

    get HierarchyId(): string {
        return this.wbPlanId + "";
    }

    get HierarchyIdUI(): string {
        return this.planRow?.cif?.PLANNING_LEVEL_ID || this.planRow?.cif?.PLAN_ID || 'Not Set';
    }

    get HierarchyName(): string {
        return this.planRow?.cif?.PLANNING_LEVEL_NAME || this.planRow?.cif?.PLAN_NAME || 'Not Set';
    }

    get bHasSortOrder(): boolean {
        return true;
    }

    get LoadMessageProvider(): IMessageProvider {
        return this._loadMessageProvider;
    }

    get bLoaded(): boolean {
        return this._bLoaded;
    }

    get OnLoadComplete(): Promise<void> {
        const self = this;

        if (self._bLoaded) return Promise.resolve();

        if (!_.isUndefined(self._OnLoadComplete)) return self._OnLoadComplete;

        const returnValue = (self._OnLoadComplete = (async () => {
            let msg = "";

            self._loadMessageProvider.clearMessages();
            self.planRow = self.dbContext.get(esWB_Plan, self.wbPlanId)
            if (_.isEmpty(self.planRow))
                self.planRow = await self.dbContext.find(esWB_Plan, self.wbPlanId)

            if (_.isEmpty(self.planRow)) {
                msg = `Unknown Plan ${self.wbPlanId}`;
                self.LoadMessageProvider.addMessage({
                    code: "MissingPlan",
                    type: MessageType.Error,
                    title: msg,
                    description: msg,
                });
                return;
            }


            let planningLevelNode: IHierarchyNode = {
                NodeId: 'PlanningLevel',
                NodeIdUI: self.planRow.cif.PLANNING_LEVEL_ID,
                NodeName: self.planRow.cif.PLANNING_LEVEL_NAME,
                NodeLevel: 'PlanningLevel',
                NodeOrder: self.planRow.cif.PLANNING_LEVEL_NAME,
                NodeShortName: self.planRow.cif.PLANNING_LEVEL_NAME,
            };
            self.dctNodes[planningLevelNode.NodeId] = planningLevelNode
            self.dctNodesByLevel['PlanningLevel'] = [planningLevelNode]

            self.arrTopLevelNodes.push(planningLevelNode)

            // Get the Product Groups
            let planPrdGrps = <WB_PlanProductGroupRow[]>await self.dbContext.findAll(esWB_Plan_Product_Group, {
                where: {
                    WB_PLAN_ID: self.wbPlanId,
                    DELETED_IND: { neq: '1' }
                }
            })
            let wbPrdGrpIds = _.map(planPrdGrps, (rel) => rel.cif.WB_PRDGRP_ID)
            let arrProductGroups: WB_ProductGroupRow[] = []
            if(!_.isEmpty(wbPrdGrpIds)) {
                arrProductGroups = await self.dbContext.findAll(esWB_Product_Group, {
                    where: { WB_PRDGRP_ID: { inq: wbPrdGrpIds } }
                });
            }

            _.forEach(arrProductGroups, (prdGrp) => {
                let childNode = self.addNode_PG(prdGrp, 'ProductGroup')
                const nodeIdChild = childNode.NodeId;
                const nodeIdParent = planningLevelNode.NodeId;


                this.dctNodeParentByChildId[nodeIdChild] = planningLevelNode;
                childNode.ParentId = planningLevelNode.NodeId;

                if (nodeIdParent in this.dctNodeChildrenByParentId) this.dctNodeChildrenByParentId[nodeIdParent].push(childNode);
                else this.dctNodeChildrenByParentId[nodeIdParent] = [childNode];

            })

            // Add Static Levels
            self.dctHierarchyLevels['PlanningLevel'] = {
                LevelId: 'PlanningLevel',
                LevelName: 'Planning Level',
                LevelDepth: 1
            }

            self.dctHierarchyLevels['ProductGroup'] = {
                LevelId: 'ProductGroup',
                LevelName: 'Product Group',
                LevelDepth: 2
            }

            //

            self._bLoaded = true;
        })());

        return returnValue;
    }



    private addNode_PG(aPrdGrpRow: WB_ProductGroupRow, sLevel: string): IHierarchyNode {
        //--
        if (_.isEmpty(aPrdGrpRow))
            return undefined

        let wbPrdGrpId = aPrdGrpRow.cif.WB_PRDGRP_ID + ''


        if (wbPrdGrpId in this.dctNodes)
            return this.dctNodes[wbPrdGrpId]

        let newNode: IHierarchyNode = {
            NodeId: wbPrdGrpId,
            NodeIdUI: aPrdGrpRow.cif.PRODUCT_GROUP_ID,
            NodeName: aPrdGrpRow.cif.PRODUCT_GROUP_NAME,
            NodeLevel: sLevel,
            NodeOrder: this.bSortById ? aPrdGrpRow.cif.PRODUCT_GROUP_ID : aPrdGrpRow.cif.PRODUCT_GROUP_NAME,
            NodeShortName: aPrdGrpRow.cif.PRODUCT_GROUP_NAME
        }

        this.dctNodes[wbPrdGrpId] = newNode

        if (sLevel in this.dctNodesByLevel)
            this.dctNodesByLevel[sLevel].push(newNode)
        else
            this.dctNodesByLevel[sLevel] = [newNode]

        return this.dctNodes[wbPrdGrpId]
    }

    getLevels(): IHierarchyLevel[] {
        return _.values(this.dctHierarchyLevels);
    }

    getTopLevelNodes(): IHierarchyNode[] {
        return this.arrTopLevelNodes;
    }

    getNode(NodeId: string): IHierarchyNode {
        if (NodeId in this.dctNodes) return this.dctNodes[NodeId];

        return undefined;
    }

    getNodes(): IHierarchyNode[] {
        return _.values(this.dctNodes);
    }

    getNodesAtLevel(HierarchyLevel: string): IHierarchyNode[] {
        if (HierarchyLevel in this.dctNodesByLevel) return this.dctNodesByLevel[HierarchyLevel];

        return [];
    }

    getChildren(nodes: IHierarchyNode[] | IHierarchyNode | string[] | string, levelId?: string): IHierarchyNode[] {
        const arrNodeIds = this.convertToKeyArray(nodes);

        const bCheckLevel = !_.isEmpty(levelId);
        const returnValue: IHierarchyNode[] = [];

        const arrRecurseOnChildren: IHierarchyNode[] = [];
        _.forEach(arrNodeIds, (aNodeId) => {
            if (aNodeId in this.dctNodeChildrenByParentId) {
                const arrChildren = this.dctNodeChildrenByParentId[aNodeId];
                _.forEach(arrChildren, (aChildNode) => {
                    if (bCheckLevel) {
                        if (levelId == aChildNode.NodeLevel) returnValue.push(aChildNode);
                        else arrRecurseOnChildren.push(aChildNode);
                    } else {
                        returnValue.push(aChildNode);
                    }
                });
            }
        });

        // Recurse
        if (bCheckLevel && !_.isEmpty(arrRecurseOnChildren)) {
            const moreChildren = this.getChildren(arrRecurseOnChildren, levelId);
            _.forEach(moreChildren, (a) => returnValue.push(a));
        }

        return returnValue;
    }

    getParents(
        nodes: IHierarchyNode[] | IHierarchyNode | string[] | string,
        levelId?: string,
    ): {
        [NodeId: string]: IHierarchyNode;
    } {
        const arrNodeIds = this.convertToKeyArray(nodes);

        const bCheckLevel = !_.isEmpty(levelId);
        const returnValue: { [NodeId: string]: IHierarchyNode } = {};

        const heightLimit = 20;
        _.forEach(arrNodeIds, (aNodeId) => {
            let depth = 0;
            let parent = this.dctNodeParentByChildId[aNodeId];
            if (bCheckLevel) {
                while (parent && parent?.NodeLevel != levelId && depth < heightLimit) {
                    parent = this.dctNodeParentByChildId[parent.NodeId];
                    depth += 1;
                }
                if (depth == heightLimit) parent = undefined;
            }

            returnValue[aNodeId] = parent;
        });

        return returnValue;
    }

    private convertToKeyArray(nodes: IHierarchyNode[] | IHierarchyNode | string[] | string): string[] {
        const returnValue: string[] = [];

        if (_.isEmpty(nodes) && !_.isNumber(nodes)) return returnValue;

        let keyList: any[];
        if (_.isArray(nodes)) keyList = nodes;
        else keyList = [nodes];

        _.forEach(keyList, (aKeyItem) => {
            if (_.isString(aKeyItem)) {
                returnValue.push(aKeyItem);
                return;
            }

            if (_.isNumber(aKeyItem)) returnValue.push(aKeyItem + "");

            if (_.isEmpty(aKeyItem)) return;

            if ("NodeId" in aKeyItem) returnValue.push(aKeyItem.NodeId);
        });

        return returnValue;
    }
}
